import { Botao } from '../../components/Botao'
import { IconeAlerta } from '../../components/Icones'
import { IconeConversa } from '../../components/IconesDeDocumento'
import { TEXTOS } from '../../lib/glossario'
import type { MensagemDaConsulta } from '../../types/documentos'
import { CitacaoDoDocumento } from './CitacaoDoDocumento'
import { blocosDaResposta } from './textoSimples'

/**
 * A resposta de uma pergunta — ou o motivo de não haver resposta.
 *
 * Quatro desfechos, e cada um diz o que é antes do texto: respondida (com as fontes), não encontrada
 * nos documentos, fora do assunto, ou falha (com o botão de perguntar de novo). A resposta sem fonte
 * válida ganha o aviso em âmbar ANTES do texto: quem lê de cima para baixo vê a ressalva primeiro.
 *
 * @param props a mensagem e o que fazer para repetir a pergunta
 * @returns a resposta
 */
export function CartaoDaResposta({
  mensagem,
  aoRepetir,
}: {
  mensagem: MensagemDaConsulta
  aoRepetir?: () => void
}) {
  const t = TEXTOS.consulta
  const resposta = mensagem.resposta

  if (mensagem.situacao === 'FALHOU' || !resposta) {
    return (
      <div className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-900 ring-1 ring-inset ring-red-200">
        <p className="flex items-center gap-2 font-medium">
          <IconeAlerta className="h-4 w-4" />
          {t.falhou}
        </p>
        {mensagem.erro && <p className="mt-1 text-red-800">{mensagem.erro}</p>}
        {aoRepetir && (
          <Botao tamanho="pequeno" className="mt-3" onClick={aoRepetir}>
            {t.perguntarDeNovo}
          </Botao>
        )}
      </div>
    )
  }

  const respondida = resposta.situacao === 'RESPONDIDA'
  return (
    <article className="rounded-xl bg-white shadow-cartao ring-1 ring-slate-200/70">
      <div className="space-y-3 px-5 py-4">
        {!respondida && (
          <p className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
            <IconeConversa className="h-3.5 w-3.5" />
            {resposta.situacao === 'NAO_ENCONTRADA' ? t.naoEncontrada : t.foraDoEscopo}
          </p>
        )}
        {resposta.semFonte && (
          <p className="flex gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-inset ring-amber-200">
            <IconeAlerta className="mt-0.5 h-4 w-4 shrink-0" />
            {t.semFonte}
          </p>
        )}
        <div className="max-w-prose space-y-2.5 text-[0.95rem] leading-relaxed text-slate-800">
          {blocosDaResposta(resposta.texto).map((bloco, indice) =>
            bloco.tipo === 'paragrafo' ? (
              <p key={indice}>{bloco.texto}</p>
            ) : (
              <ul key={indice} className="list-disc space-y-1 pl-5 marker:text-niteroi-400">
                {bloco.itens.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            ),
          )}
        </div>
      </div>
      {resposta.citacoes.length > 0 && (
        <section className="border-t border-slate-100 px-5 py-4" aria-label={t.fontes}>
          <h3 className="mb-2.5 text-xs font-semibold text-slate-500">{t.fontes}</h3>
          <ol className="space-y-2">
            {resposta.citacoes.map((citacao, indice) => (
              <CitacaoDoDocumento key={citacao.trechoId} citacao={citacao} numero={indice + 1} />
            ))}
          </ol>
        </section>
      )}
      {respondida && <p className="border-t border-slate-100 px-5 py-2.5 text-xs text-slate-500">{t.aviso}</p>}
    </article>
  )
}
